import {
  Box,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MeetingTypes } from '@llx/common';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  MeetingFieldsFragment,
  useListCaseClustersQuery,
  useListClientsQuery,
} from '@/@generated/codegen/API';
import { MEETING_DEFAULT_LOCATION } from '@/features/meeting/helpers/constants';
import {
  CreateMeetingFormData,
  meetingInformationSchema,
} from '@/pages/admin/components/CreateMeetingForm/createMeetingFormSchema';
import { AppSpinner } from '@/ui/AppSpinner';
import ItemList from '@/ui/ItemList/ItemList';
import { useFormFieldArrayHelpers } from '@/utils/hooks/useFormFieldArrayHelpers';

type Props = {
  form: UseFormReturn<CreateMeetingFormData>;
  meeting?: MeetingFieldsFragment;
  setFirstColumnValid: (value: boolean) => void;
  isDuplicateMeeting?: boolean;
};

export const CreateMeetingFormColumnOne = ({
  form,
  meeting,
  setFirstColumnValid,
  isDuplicateMeeting,
}: Props) => {
  const { t } = useTranslation();
  const {
    formState: { errors, isSubmitting },
  } = form;
  const clients = useListClientsQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
      where: {},
    },
  });
  const clientsList = useMemo(() => {
    return clients.data?.listClients.edges.map((edge) => edge.node);
  }, [clients.data]);
  const caseClusters = useListCaseClustersQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
      where: {},
    },
  });
  const caseClusterList = useMemo(() => {
    return caseClusters.data?.listCaseClusters.edges.map((edge) => edge.node);
  }, [caseClusters.data]);
  const {
    handleAdd: handleAddClient,
    handleRemove: handleRemoveClient,
    getAvailableItems: getAvailableClients,
  } = useFormFieldArrayHelpers({
    form,
    arrayFieldName: 'meetingClientIDs',
    selectorFieldName: 'selectedClientID',
    allElements: clientsList ?? [],
  });

  const currentMeetingClientIDs = form.watch('meetingClientIDs') ?? [];
  const meetingTypeWatch = form.watch('meetingType');
  const locationWatch = form.watch('location');
  const eventDateWatch = form.watch('eventDate');
  const nameWatch = form.watch('name');
  const caseClusterWatch = form.watch('caseClusterID');

  const getClientNameById = useCallback(
    (id: string) => {
      if (!clientsList) return null;
      const client = clientsList.find((client) => client.id === id);
      return client ? client.name : null;
    },
    [clientsList]
  );

  useEffect(() => {
    const result = meetingInformationSchema.safeParse({
      meetingType: form.getValues('meetingType'),
      name: form.getValues('name'),
      location: form.getValues('location'),
      eventDate: form.getValues('eventDate'),
      meetingClientIDs: form.getValues('meetingClientIDs'),
      caseClusterID: form.getValues('caseClusterID'),
    });

    setFirstColumnValid(result.success);
  }, [
    currentMeetingClientIDs,
    meetingTypeWatch,
    locationWatch,
    eventDateWatch,
    nameWatch,
    caseClusterWatch,
  ]);

  useEffect(() => {
    if (meetingTypeWatch === MeetingTypes.ONLINE) {
      form.setValue('location', MEETING_DEFAULT_LOCATION);
    } else if (!meeting) {
      form.setValue('location', '');
      form.setValue('name', '');
    }
  }, [meetingTypeWatch]);

  useEffect(() => {
    if (!clients.data) return;
    if (form.getValues('meetingType') === MeetingTypes.LIVE) {
      const clients = form
        .getValues('meetingClientIDs')
        .map((id) => getClientNameById(id))
        .join(', ');
      const location = form.getValues('location');
      const date = form.getValues('eventDate').toISOString().slice(0, 10).replace(/-/g, '');
      form.setValue('name', `${[clients, location, date].filter((elem) => !!elem).join(' - ')}`);
    }
  }, [locationWatch, currentMeetingClientIDs, eventDateWatch, clients]);

  const onClientChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      form.setValue('selectedClientID', e.target.value);
      handleAddClient();
      form.setValue('selectedClientID', '');
    },
    [form, handleAddClient]
  );

  if (!clients.data || !caseClusters.data) {
    return (
      <Flex w="full" h="full" justifyContent="center" alignItems="center">
        <AppSpinner />
      </Flex>
    );
  }

  return (
    <>
      <FormControl isRequired isInvalid={!meeting && Boolean(errors.meetingType)}>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.meetingType.label')}
        </FormLabel>
        <Select
          data-test="meeting-meetingType-dropdDown"
          aria-label="Choose a meeting type"
          {...form.register('meetingType', { required: true, deps: ['name'] })}
        >
          {Object.values(MeetingTypes).map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>

        {errors.meetingType && (
          <FormHelperText color="red">
            {t('admin.createMeeting.form.meetingType.error')}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isRequired isInvalid={Boolean(errors.name)}>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.meetingName.label')}
        </FormLabel>
        <Input
          type="text"
          aria-label="Choose a meeting name"
          {...form.register('name', { required: true })}
          placeholder="Name"
          isDisabled={form.getValues('meetingType') === MeetingTypes.LIVE}
        />
        {errors.name && (
          <FormHelperText color="red">
            {t('admin.createMeeting.form.meetingName.error')}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isRequired isInvalid={Boolean(errors.eventDate)}>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.eventDate.label')}
        </FormLabel>
        <Box
          border="1px solid"
          borderColor="livelinx.grey100"
          borderRadius="md"
          paddingTop={2}
          paddingBottom={2}
          paddingLeft={4}
          opacity={isSubmitting ? 0.5 : 1}
        >
          <DatePicker
            ariaDescribedBy="Date of the meeting"
            selected={form.getValues('eventDate')}
            onChange={(date: Date) => form.setValue('eventDate', date)}
            dateFormat="yyyy-MM-dd"
            minDate={new Date()}
          />
        </Box>
        {errors.eventDate && (
          <FormHelperText color="red">
            {t('admin.createMeeting.form.eventDate.error')}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isRequired>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.meetingClients.label')}
        </FormLabel>
        <Controller
          name="selectedClientID"
          control={form.control}
          render={({ field }) => (
            <Select
              placeholder={
                t('admin.createMeeting.form.meetingClients.placeholder') || 'Click to add a client'
              }
              aria-label="Add a client to the meeting"
              {...field}
              onChange={onClientChange}
              required={form.getValues('meetingClientIDs').length === 0}
              value={field.value ?? undefined}
            >
              {getAvailableClients().map((elem) => (
                <option key={elem.id} value={elem.id}>
                  {elem.name}
                </option>
              ))}
            </Select>
          )}
        />
        <VStack p={2} align="flex-start">
          {currentMeetingClientIDs.length && (
            <ItemList
              itemList={currentMeetingClientIDs.map((id) => ({
                id: id,
                value: getClientNameById(id)!,
              }))}
              removeItemFunc={handleRemoveClient}
            />
          )}
          {currentMeetingClientIDs.length === 0 && (
            <Text>{t('admin.createMeeting.form.meetingClients.empty')}</Text>
          )}
          {errors.meetingClientIDs && (
            <FormHelperText color="red">{errors.meetingClientIDs.message}</FormHelperText>
          )}
        </VStack>
      </FormControl>

      <FormControl isRequired isInvalid={!meeting && Boolean(errors.caseClusterID)}>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.caseCluster.label')}
        </FormLabel>
        <Select
          data-test="meeting-caseCluster-dropdDown"
          aria-label="Choose a cluster for the meeting"
          placeholder={
            t('admin.createMeeting.form.caseCluster.placeholder') || 'Select a Case Cluster'
          }
          {...form.register('caseClusterID', {
            required: !(meeting && !isDuplicateMeeting),
          })}
          defaultValue={meeting && meeting.caseClusterId}
          isDisabled={Boolean(meeting && !isDuplicateMeeting)}
        >
          {caseClusterList &&
            caseClusterList.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
        </Select>

        {errors.caseClusterID && (
          <FormHelperText color="red">
            {t('admin.createMeeting.form.meetingCaseCluster.error')}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isRequired isInvalid={Boolean(errors.location)}>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.meetingLocation.label')}
        </FormLabel>
        <Input
          type="text"
          {...form.register('location', { required: true })}
          placeholder="Location"
          isDisabled={form.getValues('meetingType') === MeetingTypes.ONLINE}
          aria-label="Location of the meeting"
        />
        {errors.location && (
          <FormHelperText color="red">
            {t('admin.createMeeting.form.meetingLocation.error')}
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
};
